import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AdminLayout, UsersPage } from './components/admin';
import AdminRoute from './components/admin/AdminRoute';
import IntegrationsRequiredRoute from './components/auth/IntegrationsRequiredRoute';
import PrivateRoute from './components/auth/PrivateRoute';
import ChatWidget from './components/common/ChatWidget';
import { FlowLayout } from './components/flow';
import Integrations from './components/integrations/Integrations';
import SettingsPage from './components/settings/SettingsPage';
import TaxReportPage from './components/tax-reports/TaxReportPage';
import TransactionPage from './components/transactions/TransactionPage';
import SmartCheckPage from './components/smart-check/SmartCheckPage';
import { AdminProvider } from './context/AdminContext';
import { useAuth } from './context/AuthContext';
import { useI18n } from './context/I18nContext'; // Added for i18n loading state
import { useEmailChange } from './hooks/useEmailChange';
import { useEmailVerification } from './hooks/useEmailVerification';
import LoadingFallback from './LoadingFallback';
import { getBetaAppLoginUrl, getBetaAppSignupUrl } from './utils/betaApp';

// Component to handle external redirects
const ExternalRedirect = ({ url }: { url: string }) => {
    window.location.href = url;
    return null;
};

const AppContent = () => {
    const location = useLocation();
    const { currentUser, isInitialized: isAuthInitialized } = useAuth(); // Aliased isInitialized
    const { isLoading: isI18nLoading } = useI18n(); // Get i18n loading state
    const { t } = useTranslation();

    useEmailVerification();
    useEmailChange(t);

    // Check if this is an admin route
    const isAdminRoute = location.pathname.startsWith('/admin');

    // No auth pages anymore - authentication handled by main CoinTracking
    const isAuthPage = false;

    // Wait for auth to be initialized before any redirects
    // Wait for auth to be initialized AND for i18n to finish loading before any redirects or main content rendering
    if (!isAuthInitialized || isI18nLoading) {
        return <LoadingFallback />;
    }

    const isAuthenticated = currentUser !== null;

    // If this is an admin route, render the admin routes directly without the main app layout
    if (isAdminRoute) {
        return (
            <AdminProvider>
                <Routes>
                    <Route path="/admin" element={<AdminRoute />}>
                        <Route element={<AdminLayout />}>
                            <Route index element={<Navigate to="/admin/users" replace />} />
                            <Route path="users" element={<UsersPage />} />
                        </Route>
                    </Route>
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </AdminProvider>
        );
    }

    const isVerifyEmailPage = location.pathname === '/verify-email';

    // Use FlowLayout for all authenticated pages (except auth pages)
    if (isAuthenticated && !isAuthPage && !isVerifyEmailPage) {
        return (
            <AdminProvider>
                <FlowLayout>
                    <Routes>
                        {/* Flow pages */}
                        <Route path="/integrations/*" element={<PrivateRoute><Integrations /></PrivateRoute>} />
                        <Route
                            path="/smart-check/*"
                            element={
                                <PrivateRoute>
                                    <IntegrationsRequiredRoute>
                                        <SmartCheckPage />
                                    </IntegrationsRequiredRoute>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/tax-reports"
                            element={
                                <PrivateRoute>
                                    <IntegrationsRequiredRoute>
                                        <TaxReportPage />
                                    </IntegrationsRequiredRoute>
                                </PrivateRoute>
                            }
                        />
                        
                        {/* All other authenticated pages now use FlowLayout too */}
                        <Route
                            path="/transactions"
                            element={
                                <PrivateRoute>
                                    <TransactionPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/settings"
                            element={
                                <PrivateRoute>
                                    <SettingsPage />
                                </PrivateRoute>
                            }
                        />
                        
                        {/* Plans managed by main CoinTracking app */}
                        <Route
                            path="/plans"
                            element={<Navigate to="https://cointracking.info/plans" replace />}
                        />
                        
                        {/* Handle the root path */}
                        <Route
                            path="/"
                            element={<Navigate to="/integrations" replace />}
                        />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </FlowLayout>
                {/* Add ChatWidget to make it available on all pages */}
                <ChatWidget />
            </AdminProvider>
        );
    }

    // Fallback for non-authenticated pages and auth pages
    return (
        <AdminProvider>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '100vh',
                    bgcolor: 'background.default',
                }}
            >
                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        minHeight: '100vh',
                        bgcolor: 'background.default',
                        overflow: 'auto',
                    }}
                >
                    <Box
                        sx={{
                            px: { xs: 2, sm: 4 },
                            py: { xs: 2, sm: 3 },
                        }}
                    >
                        <Routes>
                            {/* Auth redirects - handled by main CoinTracking */}
                            <Route path="/login" element={<ExternalRedirect url={getBetaAppLoginUrl()} />} />
                            <Route path="/signup" element={<ExternalRedirect url={getBetaAppSignupUrl()} />} />
                            
                            {/* Handle root path */}
                            <Route
                                path="/"
                                element={
                                    currentUser ? <Navigate to="/integrations" replace /> : <ExternalRedirect url={getBetaAppLoginUrl()} />
                                }
                            />
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                    </Box>
                </Box>
                {/* Add ChatWidget only for authenticated pages */}
                {isAuthenticated && !isAuthPage && <ChatWidget />}
            </Box>
        </AdminProvider>
    );
};

export default AppContent;
